import React from "react";
import Badge from "../../../../../../components/common/PL/Badge/Badge";
import Button from "../../../../../../components/common/PL/Button/Button";
import ReenviarNotificacionModal from "./ReenviarNotificacionModal";
import { AuthorizationStatus } from "./transportAuthorizationTypes";
import { useElapsedSeconds, formatMmSs } from "./useElapsedSeconds";

const RESEND_AVAILABLE_AFTER_SECONDS = 20;
// Pasada una hora el contador mm:ss deja de tener sentido (una autorización real puede llevar
// días pendiente y se vería como "Pendiente 3821:14").
const COUNTDOWN_MAX_SECONDS = 60 * 60;

type PersonaEstadoStatusProps = {
  status: AuthorizationStatus;
  requestedAt?: number;
  isValid?: boolean;
  onReenviar: () => void;
  /** Reemplaza el "Pendiente" del badge cuando lo que se espera no es la aprobación en sí (p. ej.
   * la asignación de un dock). El contador mm:ss se sigue mostrando igual. */
  pendingLabel?: string;
};

const MUTED_LABELS: Partial<Record<AuthorizationStatus, string>> = {
  used: "Utilizada",
  canceled: "Cancelada",
};

const PersonaEstadoStatus: React.FC<PersonaEstadoStatusProps> = ({
  status,
  requestedAt,
  isValid = true,
  onReenviar,
  pendingLabel,
}) => {
  // Sin fallback estable, `requestedAt ?? Date.now()` cambiaría en cada render y reiniciaría
  // el intervalo del contador continuamente.
  const fallbackSince = React.useRef(Date.now()).current;
  const since = requestedAt && !isNaN(requestedAt) ? requestedAt : fallbackSince;
  const elapsedSeconds = useElapsedSeconds(since);
  const [isReenviarModalOpen, setIsReenviarModalOpen] = React.useState(false);

  if (status === "approved") {
    // Vencida: la fila ya muestra el badge "Fuera de vigencia" (VigenciaRow); no duplicar acá.
    return isValid ? <Badge variant="green" label="Autorización aprobada" /> : null;
  }

  if (status === "rejected") {
    return (
      <div className="transportPersonaEstadoRechazada">
        <Badge variant="red" label="Autorización rechazada" />
        <Button variant="outline" size="small" onClick={() => setIsReenviarModalOpen(true)}>
          Reenviar notificación
        </Button>
        <ReenviarNotificacionModal
          open={isReenviarModalOpen}
          onClose={() => setIsReenviarModalOpen(false)}
          onSubmit={() => {
            setIsReenviarModalOpen(false);
            onReenviar();
          }}
        />
      </div>
    );
  }

  if (status === "expired") {
    // Misma info que el badge "Fuera de vigencia" de VigenciaRow: no duplicar acá salvo que,
    // por falta de fechas válidas, ese badge no se haya podido mostrar.
    return isValid ? <Badge variant="grey" label="Vencida" /> : null;
  }

  const mutedLabel = MUTED_LABELS[status];
  if (mutedLabel) {
    return <Badge variant="grey" label={mutedLabel} />;
  }

  const canResend = elapsedSeconds >= RESEND_AVAILABLE_AFTER_SECONDS;
  const countdown =
    elapsedSeconds > COUNTDOWN_MAX_SECONDS ? "" : ` ${formatMmSs(elapsedSeconds)}`;
  const label = `${pendingLabel ?? "Pendiente"}${countdown}`;

  return (
    <div className="transportPersonaEstadoPendiente">
      <Badge variant="orange" label={label} />
      {canResend && (
        <Button variant="outline" size="small" onClick={onReenviar}>
          Reenviar notificación
        </Button>
      )}
    </div>
  );
};

export default PersonaEstadoStatus;
